// react and js
import PropTypes from "prop-types";
import { React, useEffect, useContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// mui
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme, styled } from "@mui/material/styles";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  List,
  ListItemIcon,
  ListItemButton,
} from "@mui/material";

// components
import Iconify from "../../components/Iconify";

// hooks
import defines from "../../hooks/defines";
import features from "../../hooks/features";
import appContext from "../../hooks/appContext";

// layouts

// pages

// theme

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
const FEATURE_TITLE_KEY_BY_PATH = {
  "/subscriptions": "layout.sidebar.features.subscriptions",
  "/downloads": "layout.sidebar.features.downloads",
  "/accounting": "layout.sidebar.features.accounting",
  "/distributor-portal": "layout.sidebar.features.distributorPortal",
  "/evaluation-portal": "layout.sidebar.features.evaluationPortal",
  "application-settings": "layout.sidebar.features.applicationSettings",
};

const StyledNavigationListItemIcon = styled(ListItemIcon)(() => ({
  color: "inherit",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledNavigationListItemButton = styled(ListItemButton)(({ theme }) => ({
  color: "#2E2E2E",
  backgroundColor: "transparent",
  //
  height: 45,
  borderRadius: "10px",
  padding: "0px 0px 0px 0px",
  margin: "4px 10px 10px 10px",

  [theme.breakpoints.up("lg")]: {
    height: 35,
    padding: "0px 0px 0px 0px",
    margin: "4px 0px 0px 0px",
  },
  //
  "&:hover": {
    backgroundColor: "#F2F2F2",
  },
}));

function SelectableStyledNavigationListItemButton({
  typographyVariant,
  featureTitle,
  featurePath,
  featureIcon,
}) {
  SelectableStyledNavigationListItemButton.propTypes = {
    typographyVariant: PropTypes.any.isRequired,
    featureTitle: PropTypes.any.isRequired,
    featurePath: PropTypes.any.isRequired,
    featureIcon: PropTypes.any.isRequired,
  };

  const { pathname } = useLocation();

  let isActiveRoot = false;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  if (pathname.includes(featurePath) === true) {
    isActiveRoot = true;
  }

  if (pathname === "/" && featurePath === "/subscriptions") {
    isActiveRoot = true;
  }

  return (
    <StyledNavigationListItemButton
      component={RouterLink}
      to={featurePath}
      sx={{
        color: isActiveRoot ? "black" : null,
        background: isActiveRoot ? "#E6E6E6" : null,
      }}
    >
      <StyledNavigationListItemIcon>
        <Iconify
          icon={featureIcon}
          minHeight={
            isDesktop
              ? defines.sidebarIconWidthHeightDesktop
              : defines.sidebarIconWidthHeightMobile
          }
          minWidth={
            isDesktop
              ? defines.sidebarIconWidthHeightDesktop
              : defines.sidebarIconWidthHeightMobile
          }
        />
      </StyledNavigationListItemIcon>
      <Typography variant={typographyVariant}>{featureTitle}</Typography>
    </StyledNavigationListItemButton>
  );
}

function NavigationSection({ sidebarItems, typographyVariant }) {
  NavigationSection.propTypes = {
    sidebarItems: PropTypes.any.isRequired,
    typographyVariant: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <List
      sx={{
        background: "transparent",
        padding: isDesktop ? "9px 6px 0px 7px" : "9px 0px 0px 0px",
      }}
    >
      <>
        {sidebarItems
          .map((feature, index) => (
          <Box key={index}>
            {feature.path === "/subscriptions" ||
            feature.path === "/accounting" ||
            (stateStorage.userInformation.account_settings
              .dashboard_features !== undefined &&
              stateStorage.userInformation.account_settings
                .dashboard_features[feature.backendLinking] ===
                feature.trueObfs) ? (
              <SelectableStyledNavigationListItemButton
                typographyVariant={typographyVariant}
                featureTitle={
                  FEATURE_TITLE_KEY_BY_PATH[feature.path]
                    ? t(FEATURE_TITLE_KEY_BY_PATH[feature.path])
                    : feature.title
                }
                featurePath={feature.path}
                featureIcon={feature.icon}
                currentPath={pathname}
              />
            ) : null}

            {feature.path === "/accounting" && (
              <SelectableStyledNavigationListItemButton
                typographyVariant={typographyVariant}
                featureTitle={t("layout.sidebar.features.userProfile")}
                featurePath="/account-settings"
                featureIcon="eva:person-fill"
                currentPath={pathname}
              />
            )}
          </Box>
        ))}
      </>
    </List>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function PrivateSidebar({ isOpenSidebar, onCloseSidebar }) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  PrivateSidebar.propTypes = {
    isOpenSidebar: PropTypes.any.isRequired,
    onCloseSidebar: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const myLocalStateStorage = JSON.parse(
    localStorage.getItem("myLocalStateStorage")
  );

  return (
    myLocalStateStorage !== null &&
    stateStorage.userInformation !== "" && (
      <>
        {!isDesktop && (
          <Drawer
            open={isOpenSidebar}
            onClose={onCloseSidebar}
            anchor="right"
            PaperProps={{
              sx: {
                zIndex: 0,
                top: 0,
                width: defines.sidebarWidthMobile,
                borderLeft: "1px solid #C8C8C8",
                borderRight: "none",
                borderColor: "#C8C8C8",
              },
            }}
          >
            <Box
              sx={{
                background: "#F2F2F2",
                display: "flex",
                minHeight: defines.headerHeightMobile,
                boxShadow: "0px 0px 2px rgba(0,0,0,0.5)",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                pr: "12px",
              }}
            >
              <Typography
                variant="contentBigBold"
                sx={{
                  fontWeight: "bold",
                  color: "#2E2E2E",
                }}
              >
                {t("layout.sidebar.menuTitle")}
              </Typography>

              <IconButton
                onClick={onCloseSidebar}
                sx={{ borderRadius: "10px" }}
              >
                <Iconify
                  icon="eva:close-outline"
                  minWidth={35}
                  minHeight={35}
                  color="#2E2E2E"
                />
              </IconButton>
            </Box>

            <NavigationSection
              sidebarItems={features}
              typographyVariant="sidebarMobile"
            />
          </Drawer>
        )}

        {isDesktop && (
          <Drawer
            open
            variant="permanent"
            PaperProps={{
              sx: {
                zIndex: 0,
                background: "white",
                top: defines.headerHeightDesktop,
                width: defines.sidebarWidthDesktop,
                borderRight: "1px solid #C8C8C8",
                borderColor: "#C8C8C8",
              },
            }}
          >
            <NavigationSection
              sidebarItems={features}
              typographyVariant="sidebarDesktop"
            />

            <Box sx={{ minHeight: defines.headerHeightMobile }} />
          </Drawer>
        )}
      </>
    )
  );
}
